import {
  ContentChildren,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SecurityContext,
  SimpleChange,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TIPPY_ITEM_CLASS, TippyItemDirective } from '@app/shared/directives/tippy-item.directive';
import { isTruncated } from '@app/utils/is-truncated';
import { ResizeSensor } from '@app/utils/resize-sensor';
import { isNotBlank } from '@app/utils/string';
import tippy, { Instance, Placement, Props, sticky } from 'tippy.js';

@Directive({
  exportAs: 'tippy',
  selector: '[portalTippy]',
  standalone: false,
})
export class TippyDirective implements OnChanges, OnDestroy, OnInit {
  @Input()
  appendTo: 'body' | 'parent' | Element | ((ref: Element) => Element) = 'parent';

  @Input()
  arrow: boolean = true;

  @Input()
  delay: number | [number | null, number | null] = 250;

  @Input()
  distance: number = 10;

  @Input()
  duration: number | [number | null, number | null] = 0;

  @Input()
  hideOnClick: boolean | 'toggle' = true;

  /**
   * If the tooltip content should be handled as a html string
   */
  @Input()
  html: boolean = false;

  @Input()
  interactive: boolean = false;

  @Input()
  portalTippy!: string | TemplateRef<any>;

  @Input()
  placement: Placement = 'top';

  @Input()
  popoverClass?: string;

  @Input()
  set popover(value: boolean | undefined) {
    if (value !== false) {
      this.interactive = true;
      this.isPopover = true;
    }
  }

  @Input()
  set showIfTruncated(value: boolean | undefined) {
    if (value !== false) {
      this.showOnlyIfElementIsTruncated = true;
    }
  }

  @Input()
  sticky: boolean | 'reference' | 'popper' = false;

  @Input({
    transform: (value?: string) => value ?? 'light-border',
  })
  theme: string = 'light-border';

  @Input()
  tippyClass: string = '';

  @Input()
  tippyDisabled: boolean = false;

  @Input()
  trigger: 'mouseenter' | 'focus' | 'focusin' | 'click' | 'manual' = 'mouseenter';

  @Input()
  triggerTarget?: Element;

  @Output()
  hidden: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  openChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  shown: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  triggered: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  untriggered: EventEmitter<void> = new EventEmitter<void>();

  @ContentChildren(TippyItemDirective)
  public readonly items: TippyItemDirective[];

  private animationFrameRequest?: number;

  /**
   * If this is a popover then the padding will be removed and the popover will be interactive
   */
  private isPopover: boolean = false;
  private tippyInstance?: Instance;

  private showOnlyIfElementIsTruncated: boolean = false;

  private resizeCallback = () => {
    this.destroyTippy();
    this.createTippyInstance();
  };

  private resizeSensor?: ResizeSensor;

  /**
   * ViewRef of the created embedded view of a given ng-template (if a templateRef was passed
   * instead of a string).
   */
  private viewRef?: EmbeddedViewRef<any>;

  private _isVisible: boolean = false;

  private hideTippy = () => {
    this.hide();
  };

  private tippyItems?: NodeListOf<Element>;

  get isVisible(): boolean {
    return this._isVisible;
  }

  constructor(
    private domSanitizer: DomSanitizer,
    private el: ElementRef<HTMLElement>,
    private viewContainer: ViewContainerRef,
    private zone: NgZone
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.tippyDisabled?.firstChange === false) {
      const disabled: SimpleChange = changes.tippyDisabled;

      if (disabled.previousValue === true && disabled.currentValue === false) {
        this.createTippyInstance();
      } else if (disabled.currentValue === true) {
        this.destroyTippy();
      }
    } else {
      // Update tippy properties if any prop changed and the instance has not been disabled
      const tippyIsNowDisabled = changes.tippyDisabled?.currentValue ?? false;

      if (this.tippyInstance !== undefined && tippyIsNowDisabled === false) {
        requestAnimationFrame(() => {
          this.tippyInstance.setProps(this.getTippyProps());
        });
      }
    }

    // Content changed
    if (changes.portalTippy?.firstChange === false && this.showOnlyIfElementIsTruncated) {
      requestAnimationFrame(this.resizeCallback);
    }
  }

  ngOnDestroy(): void {
    this.destroyResizeSensor();
    this.destroyTippy();

    if (this.animationFrameRequest) {
      cancelAnimationFrame(this.animationFrameRequest);
    }
  }

  ngOnInit(): void {
    // Reset default delay for popovers
    if (this.isPopover && this.delay === 250) {
      this.delay = 0;
    }

    if (this.tippyDisabled !== true) {
      this.createTippyInstance();
    }

    // Watch for resizing of the browser window in case the tippy should only be shown if the
    // element is truncated.
    if (this.showOnlyIfElementIsTruncated === true) {
      // tslint:disable-next-line:no-unused-expression
      this.resizeSensor = new ResizeSensor(document.body, this.resizeCallback, 1000);
    }
  }

  public hide(duration?: number): void {
    if (this.tippyInstance != null && this.tippyInstance.state.isDestroyed === false) {
      if (duration) {
        this.tippyInstance.setProps({ duration: duration });
        this.tippyInstance.hide();
      } else {
        this.tippyInstance.hide();
      }
    }
  }

  public show(duration?: number): void {
    if (duration) {
      this.tippyInstance?.setProps({ duration: duration });
      this.tippyInstance?.show();
    } else {
      this.tippyInstance?.show();
    }
  }

  public toggle(duration?: number): void {
    if (this.isVisible) {
      this.hide(duration);
    } else {
      this.show(duration);
    }
  }

  public update(): void {
    this.tippyInstance?.popperInstance.update();
  }

  private createTippyInstance(): void {
    const options = this.getTippyProps();

    this.zone.runOutsideAngular(() => {
      if (this.showOnlyIfElementIsTruncated) {
        const initialise = () => {
          // In some cases the directive is evaluated before the element is appended to the dom. If this
          // happens then the truncated check does not work because the width will be 0. So in this case
          // let's check in the next animation frame callback if the width is no longer 0.
          if (this.showOnlyIfElementIsTruncated === true) {
            if (this.el.nativeElement.offsetWidth === 0) {
              // Only rerun the check if the directive has not been destroyed
              if (this.resizeSensor != null) {
                this.animationFrameRequest = requestAnimationFrame(initialise);
              }
            } else if (isTruncated(this.el.nativeElement) === true) {
              this.tippyInstance = tippy(this.el.nativeElement, options);
            }
          } else {
            this.tippyInstance = tippy(this.el.nativeElement, options);
          }
        };

        this.animationFrameRequest = requestAnimationFrame(initialise);
      } else {
        this.tippyInstance = tippy(this.el.nativeElement, options);
      }
    });
  }

  private destroyResizeSensor(): void {
    if (this.resizeSensor != null) {
      this.resizeSensor.detach(this.resizeCallback);
      this.resizeSensor = null;
    }
  }

  private destroyTippy(): void {
    if (this.tippyInstance != null && this.tippyInstance.state.isDestroyed === false) {
      this.tippyInstance.destroy();
    }

    if (this.viewRef != null) {
      this.viewRef.destroy();
      this.viewRef = null;
    }

    if (this.animationFrameRequest != null) {
      cancelAnimationFrame(this.animationFrameRequest);
    }
  }

  private getTippyProps() {
    // Handle 'body' specifically because it's not allowed by tippy but we use it to make it easier
    // to append the tippy to the body.
    const appendTo = this.appendTo === 'body' ? document.body : this.appendTo;

    const options: Partial<Props> = {
      appendTo,
      allowHTML: this.html !== false,
      arrow: this.arrow,
      delay: this.delay,
      offset: [0, this.distance],
      duration: this.duration,
      hideOnClick: this.hideOnClick,
      interactive: this.interactive,
      placement: this.placement,
      plugins: [sticky],
      sticky: this.sticky,
      trigger: this.trigger,
      theme: this.theme,
      popperOptions: {
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              // e.g. `filter-input.component` has one tippy opening another.
              // so that the second content is visible, on small devices, it must be placed over the first one.
              // this does exactly that. otherwise the second content overflows and is not entirely visible on small devices.
              altAxis: true,
              tether: false,
            },
          },
        ],
      },

      onHidden: () => {
        this.hidden.emit();
        this.updateVisibility(false);

        this.removeTippyItemListeners();
      },

      onShow: (instance) => {
        if (this.isPopover) {
          instance.popper.firstElementChild.classList.add('tippy-popover');
        }

        if (this.popoverClass !== undefined) {
          this.popoverClass.split(' ').forEach((className) => {
            instance.popper.firstElementChild.classList.add(className);
          });
        }

        if (isNotBlank(this.tippyClass)) {
          this.tippyClass.split(' ').forEach((className) => {
            instance.popper.firstElementChild.classList.add(className);
          });
        }

        this.addTippyItemListeners(instance);
      },

      onShown: () => {
        this.shown.emit();
        this.updateVisibility(true);
      },

      onTrigger: () => {
        this.triggered.emit();
      },

      onUntrigger: () => {
        this.untriggered.emit();
      },
    };

    if (this.isPopover) {
      options.maxWidth = 'none';
    }

    if (typeof this.portalTippy === 'string') {
      if (this.html) {
        options.content = this.domSanitizer.sanitize(SecurityContext.HTML, this.portalTippy);
      } else {
        options.content = this.portalTippy;
      }
    } else if (this.portalTippy != null) {
      this.viewRef = this.viewContainer.createEmbeddedView(this.portalTippy);

      options.content = this.viewRef.rootNodes[0];
    }

    if (this.triggerTarget !== undefined) {
      options.trigger = 'click';
      options.triggerTarget = this.triggerTarget;
    }
    return options;
  }

  private addTippyItemListeners(instance: Instance): void {
    // this.tippyItems = instance.popperChildren.content.querySelectorAll(`.${TIPPY_ITEM_CLASS}`)
    this.tippyItems = instance.popper.firstElementChild.querySelectorAll(`.${TIPPY_ITEM_CLASS}`);

    // this.logger.debug(`Adding click listeners to tippy items`, this.tippyItems)

    this.tippyItems.forEach((item) => {
      item.addEventListener('click', this.hideTippy);
    });
  }

  private removeTippyItemListeners(): void {
    if (this.tippyItems != null) {
      // this.logger.debug('Removing click listeners of tippy items', this.tippyItems)

      this.tippyItems.forEach((item) => {
        item.removeEventListener('click', this.hideTippy);
      });

      this.tippyItems = undefined;
    }
  }

  private updateVisibility(visible: boolean) {
    this._isVisible = visible;

    this.openChange.emit(visible);
  }
}
